import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendSampleSpecification,
	FrontendUserInfo,
	FrontendUserLicense,
} from "node-library/dist/portal";
import {
	CommonSwitch,
	CustomRadioGroup,
	UserInfoTextInput,
} from "../../common-components/inputs";
import { Card, Divider } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import {
	CompanyIcon,
	DeleteIcon,
	InvoiceIcon,
	LockIcon,
	UserIcon,
} from "../../common-components/svgIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { FrontendProfileTab, Severity } from "node-library/dist/constants";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import {
	BlueButton,
	OutlinedButton,
	TextButton,
} from "../../common-components/button";
import AddIcon from "@mui/icons-material/Add";

import ChangePasswordModal from "./components/ChangePasswordModal";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import UserClientInfoSection from "./userClientInfoSection";
import UserBillingInfoSection from "./userBillingInfoSection";
import UserLicenseInfoSection from "./userLicenseInfoSection";
import { GreenTickIcon } from "../../common-components/toast/successIcons";
import { ButtonTextLoadingAnimation } from "../../common-components/animation/ButtonLoadingAnimation";
import { validateEmail } from "../../helpers/inputHelper";
import UserSpecificationsSection from "./userSpecificationsSection";

interface Props {
	user: FrontendUserInfo | undefined;
	setUser: React.Dispatch<React.SetStateAction<FrontendUserInfo | undefined>>;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function UserProfilePage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const user = props.user;
	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentTab, setCurrentTab] = useState<FrontendProfileTab>(
		FrontendProfileTab.PROFILE
	);
	const [loading, setLoading] = useState<boolean>(false);

	const [firstName, setFirstName] = useState<string>(user?.firstName || "");
	const [lastName, setLastName] = useState<string>(user?.lastName || "");
	//client address info
	const [clientCompanyName, setClientCompanyName] = useState<string>(
		user?.clientCompanyName || ""
	);
	const [address, setAddress] = useState<string>(user?.address || "");
	const [city, setCity] = useState<string>(user?.city || "");
	const [province, setProvince] = useState<string>(user?.province || "");
	const [postalCode, setPostalCode] = useState<string>(user?.postalCode || "");
	const [country, setCountry] = useState<string>(user?.country || "");
	const [phoneNumber, setPhoneNumber] = useState<string>(
		user?.phoneNumber || ""
	);

	const [timezone, setTimezone] = useState<string>(
		user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
	);

	const [enableNotificationEmail, setEnableNotificationEmail] =
		useState<boolean>(user?.enableNotificationEmail || false);
	const [defaultUseSameBillingInfo, setDefaultUseSameBillingInfo] =
		useState<boolean>(!(user?.defaultUseDifferentBillingInfo || false));

	const [defaultBillingName, setDefaultBillingName] = useState<string>(
		user?.defaultBillingName || ""
	);
	const [defaultBillingAddress, setDefaultBillingAddress] = useState<string>(
		user?.defaultBillingAddress || ""
	);
	const [defaultBillingCity, setDefaultBillingCity] = useState<string>(
		user?.defaultBillingCity || ""
	);
	const [defaultBillingProvince, setDefaultBillingProvince] = useState<string>(
		user?.defaultBillingProvince || ""
	);
	const [defaultBillingPostalCode, setDefaultBillingPostalCode] =
		useState<string>(user?.defaultBillingPostalCode || "");
	const [defaultBillingCountry, setDefaultBillingCountry] = useState<string>(
		user?.defaultBillingCountry || ""
	);
	const [defaultBillingPhoneNumber, setDefaultBillingPhoneNumber] =
		useState<string>(user?.defaultBillingPhoneNumber || "");
	const [defaultBillingEmail, setDefaultBillingEmail] = useState<string>(
		user?.defaultBillingEmail || ""
	);

	const [testReportEmails, setTestReportEmails] = useState<Array<string>>(
		user?.defaultTestReportCopyEmails || []
	);

	const [billingInfoEmails, setBillingInfoEmails] = useState<Array<string>>(
		user?.defaultBillingInfoCopyEmails || []
	);

	const [userLicenses, setUserLicenses] = useState<Array<FrontendUserLicense>>(
		[]
	);
	const [userSpecifications, setUserSpecifications] = useState<
		Array<FrontendSampleSpecification>
	>([]);

	//password change
	const [changePasswordModalOpen, setChangePasswordModalOpen] =
		useState<boolean>(false);

	//validations
	const [firstNameErrorMsg, setFirstNameErrorMsg] = useState<string>("");
	const [lastNameErrorMsg, setLastNameErrorMsg] = useState<string>("");
	//client address info
	const [clientCompanyNameErrorMsg, setClientCompanyNameErrorMsg] =
		useState<string>("");

	const [addressErrorMsg, setAddressErrorMsg] = useState<string>("");
	const [cityErrorMsg, setCityErrorMsg] = useState<string>("");
	const [provinceErrorMsg, setProvinceErrorMsg] = useState<string>("");
	const [postalCodeErrorMsg, setPostalCodeErrorMsg] = useState<string>("");
	const [countryErrorMsg, setCountryErrorMsg] = useState<string>("");
	const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState<string>("");

	const [defaultBillingNameErrorMsg, setDefaultBillingNameErrorMsg] =
		useState<string>("");
	const [defaultBillingAddressErrorMsg, setDefaultBillingAddressErrorMsg] =
		useState<string>("");
	const [defaultBillingCityErrorMsg, setDefaultBillingCityErrorMsg] =
		useState<string>("");
	const [
		defaultBillingPostalCodeErrorMsg,
		setDefaultBillingPostalCodeErrorMsg,
	] = useState<string>("");
	const [defaultBillingProvinceErrorMsg, setDefaultBillingProvinceErrorMsg] =
		useState<string>("");
	const [defaultBillingCountryErrorMsg, setDefaultBillingCountryErrorMsg] =
		useState<string>("");
	const [
		defaultBillingPhoneNumberErrorMsg,
		setDefaultBillingPhoneNumberErrorMsg,
	] = useState<string>("");
	const [defaultBillingEmailErrorMsg, setDefaultBillingEmailErrorMsg] =
		useState<string>("");

	const [testReportEmailsErrorMsgs, setTestReportEmailsErrorMsgs] = useState<
		Array<string>
	>([]);
	const [billingInfoEmailsErrorMsgs, setBillingInfoEmailsErrorMsgs] = useState<
		Array<string>
	>([]);

	async function getUserProfileInfo() {
		setLoading(true);

		try {
			const response = await sendGETRequest(
				"/profile/get-user-license-and-specs",
				{}
			);

			const licenses: Array<FrontendUserLicense> = response?.licenseInfo;
			const specifications: Array<FrontendSampleSpecification> =
				response?.specifications;
			setUserLicenses(licenses);
			setUserSpecifications(specifications);

			setLoading(false);
		} catch (err) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	useEffect(() => {
		getUserProfileInfo();
	}, []);

	function resetUserStatesToDefault(user: FrontendUserInfo) {
		setFirstName(user?.firstName || "");
		setLastName(user?.lastName || "");
		setClientCompanyName(user?.clientCompanyName || "");
		setAddress(user?.address || "");
		setCity(user?.city || "");
		setProvince(user?.province || "");
		setPostalCode(user?.postalCode || "");
		setCountry(user?.country || "");
		setPhoneNumber(user?.phoneNumber || "");
		setTimezone(
			user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
		);

		setDefaultUseSameBillingInfo(
			!(user?.defaultUseDifferentBillingInfo || false)
		);

		setDefaultBillingName(user?.defaultBillingName || "");
		setDefaultBillingAddress(user?.defaultBillingAddress || "");
		setDefaultBillingCity(user?.defaultBillingCity || "");
		setDefaultBillingProvince(user?.defaultBillingProvince || "");
		setDefaultBillingPostalCode(user?.defaultBillingPostalCode || "");
		setDefaultBillingCountry(user?.defaultBillingCountry || "");
		setDefaultBillingPhoneNumber(user?.defaultBillingPhoneNumber || "");
		setDefaultBillingEmail(user?.defaultBillingEmail || "");

		setTestReportEmails(user?.defaultTestReportCopyEmails || []);
		setBillingInfoEmails(user?.defaultBillingInfoCopyEmails || []);
	}

	function discardChanges() {
		getUserProfileInfo();
		resetUserStatesToDefault(props.user as FrontendUserInfo);
		setUnsavedChanges(false);
		openSnackbar(
			translation("ChangesDiscarded"),
			Severity.SUCCESS,
			undefined,
			<GreenTickIcon />
		);
	}

	const clientInformationFields: Array<any> = [
		{
			id: "profile-email-input",
			label: `${translation("Email")}`,
			inputType: "email",
			value: user?.userName,
			disabled: true,
			setErrorMsg: () => {},
		},
		{
			id: "profile-firstname-input",

			label: `${translation("FirstName")}`,
			inputType: "text",
			value: firstName,
			setValue: setFirstName,
			required: true,
			errorMsg: firstNameErrorMsg,
			setErrorMsg: setFirstNameErrorMsg,
		},
		{
			id: "profile-lastname-input",

			label: `${translation("LastName")}`,
			inputType: "text",
			value: lastName,
			setValue: setLastName,
			required: true,
			errorMsg: lastNameErrorMsg,
			setErrorMsg: setLastNameErrorMsg,
		},
		{
			id: "profile-phone-input",

			label: `${translation("PhoneNumber")}`,
			inputType: "tel",
			value: phoneNumber,
			setValue: setPhoneNumber,
			required: true,
			errorMsg: phoneNumberErrorMsg,
			setErrorMsg: setPhoneNumberErrorMsg,
		},
	];

	const companyInformationFields1: Array<any> = [
		{
			id: "register-company-name-input",

			label: `${translation("CompanyName")}`,
			inputType: "text",
			value: clientCompanyName,
			setValue: setClientCompanyName,
			required: true,
			errorMsg: clientCompanyNameErrorMsg,
			setErrorMsg: setClientCompanyNameErrorMsg,
		},
		{
			id: "register-address-input",

			label: `${translation("Address")}`,
			inputType: "text",
			value: address,
			setValue: setAddress,
			required: true,
			errorMsg: addressErrorMsg,
			setErrorMsg: setAddressErrorMsg,
		},
	];
	const companyInformationFields2: Array<any> = [
		{
			id: "register-city-input",

			label: `${translation("City")}`,
			inputType: "text",
			value: city,
			setValue: setCity,
			required: true,
			errorMsg: cityErrorMsg,
			setErrorMsg: setCityErrorMsg,
		},
		{
			id: "register-postal-code-input",
			label: `${translation("PostalCodeOrZip")}`,
			inputType: "text",
			value: postalCode,
			setValue: setPostalCode,
			required: true,
			errorMsg: postalCodeErrorMsg,
			setErrorMsg: setPostalCodeErrorMsg,
		},
	];

	const companyInfoForCountryAndProvince = [
		{
			id: "profile-country-input",
			label: `${translation("Country")}`,
			inputType: "text",
			value: country,
			setValue: setCountry,
			required: true,
			errorMsg: countryErrorMsg,
			setErrorMsg: setCountryErrorMsg,
		},
		{
			id: "profile-province-input",

			label: `${translation("ProvinceOrState")}`,
			inputType: "text",
			value: province,
			setValue: setProvince,
			required: true,
			errorMsg: provinceErrorMsg,
			setErrorMsg: setProvinceErrorMsg,
		},
	];

	const billingInformationFields1: Array<any> = [
		{
			id: "default-billing-name-input",
			label: translation("PersonalOrCompanyName"),
			setValue: (value: string) => {
				setDefaultBillingName(value);
			},
			value: defaultBillingName,
			predicates: [defaultBillingName && defaultBillingName.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingNameErrorMsg,
			setErrorMsg: setDefaultBillingNameErrorMsg,
		},
		{
			id: "default-billing-address-input",
			label: translation("PersonalOrCompanyAddress"),
			setValue: (value: string) => {
				setDefaultBillingAddress(value);
			},
			value: defaultBillingAddress,
			predicates: [defaultBillingAddress && defaultBillingAddress.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingAddressErrorMsg,
			setErrorMsg: setDefaultBillingAddressErrorMsg,
		},
	];
	const billingInformationFields2: Array<any> = [
		{
			id: "default-billing-city-input",
			label: translation("City"),
			setValue: (value: string) => {
				setDefaultBillingCity(value);
			},
			value: defaultBillingCity,
			predicates: [defaultBillingCity && defaultBillingCity.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingCityErrorMsg,
			setErrorMsg: setDefaultBillingCityErrorMsg,
		},
		{
			id: "default-billing-postal-code-input",
			label: translation("PostalCode"),
			setValue: (value: string) => {
				setDefaultBillingPostalCode(value);
			},
			value: defaultBillingPostalCode,
			predicates: [
				defaultBillingPostalCode && defaultBillingPostalCode.length > 0,
			],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingPostalCodeErrorMsg,
			setErrorMsg: setDefaultBillingPostalCodeErrorMsg,
		},
	];
	const billingInfoForCountryAndProvince = [
		{
			id: "default-billing-country-input",
			label: translation("Country"),
			setValue: (value: string) => {
				setDefaultBillingCountry(value);
			},
			value: defaultBillingCountry,
			predicates: [defaultBillingCountry && defaultBillingCountry.length > 0],

			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingCountryErrorMsg,
			setErrorMsg: setDefaultBillingCountryErrorMsg,
		},
		{
			id: "default-billing-province-input",
			label: translation("Province"),
			setValue: (value: string) => {
				setDefaultBillingProvince(value);
			},
			value: defaultBillingProvince,
			predicates: [defaultBillingProvince && defaultBillingProvince.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingProvinceErrorMsg,
			setErrorMsg: setDefaultBillingProvinceErrorMsg,
		},
	];
	const billingInformationFields3: Array<any> = [
		{
			id: "default-billing-phone-number-input",
			label: translation("PhoneNumber"),
			setValue: (value: string) => {
				setDefaultBillingPhoneNumber(value);
			},
			value: defaultBillingPhoneNumber,
			predicates: [
				defaultBillingPhoneNumber && defaultBillingPhoneNumber.length > 0,
			],

			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingPhoneNumberErrorMsg,
			setErrorMsg: setDefaultBillingPhoneNumberErrorMsg,
		},
		{
			id: "default-billing-email-input",
			label: translation("Email"),
			setValue: (value: string) => {
				setDefaultBillingEmail(value);
			},
			value: defaultBillingEmail,
			predicates: [defaultBillingEmail && defaultBillingEmail.length > 0],

			errorMsgs: [translation("MissingInformation")],
			errorMsg: defaultBillingEmailErrorMsg,
			setErrorMsg: setDefaultBillingEmailErrorMsg,
		},
	];
	const handleSubmitUserInfo = async () => {
		let profilePageError: boolean = false;
		//validate profile
		for (const field of [
			...clientInformationFields,
			...companyInformationFields1,
			...companyInformationFields2,
			...companyInfoForCountryAndProvince,
		]) {
			field.setErrorMsg("");
			if (
				field.required &&
				(!field.value || (field.value as string).trim().length == 0)
			) {
				field.setErrorMsg(translation("MissingInformation"));
				profilePageError = true;
			}
		}
		const resetArray = testReportEmailsErrorMsgs.map(() => "");

		for (let i = 0; i < testReportEmails.length; i++) {
			if (!validateEmail(testReportEmails[i])) {
				resetArray[i] = translation("InvalidEmail");
				profilePageError = true;
			}
		}
		setTestReportEmailsErrorMsgs(resetArray);

		if (phoneNumber.trim().length < 7) {
			setPhoneNumberErrorMsg("InvalidPhoneNumber");
			profilePageError = true;
		}

		if (profilePageError) {
			setCurrentTab(FrontendProfileTab.PROFILE);
			return;
		}

		//validate billing
		let billingPageError: boolean = false;
		if (!defaultUseSameBillingInfo) {
			for (const field of [
				...billingInformationFields1,
				...billingInformationFields2,
				...billingInformationFields3,
				...billingInfoForCountryAndProvince,
			]) {
				field.setErrorMsg("");
				if (!field.value || (field.value as string).trim().length == 0) {
					field.setErrorMsg(translation("MissingInformation"));
					billingPageError = true;
				}
			}
			if (!validateEmail(defaultBillingEmail)) {
				setDefaultBillingEmailErrorMsg(translation("InvalidEmail"));
				billingPageError = true;
			}
		}

		const billingResetArray = billingInfoEmailsErrorMsgs.map(() => "");

		for (let i = 0; i < billingInfoEmails.length; i++) {
			if (!validateEmail(billingInfoEmails[i])) {
				billingResetArray[i] = translation("InvalidEmail");
				billingPageError = true;
			}
		}
		setBillingInfoEmailsErrorMsgs(billingResetArray);
		if (billingPageError) {
			setCurrentTab(FrontendProfileTab.BILLING);
			return;
		}

		try {
			setLoading(true);
			const response = await sendPOSTRequest("/profile/update-user-profile", {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				clientCompanyName: clientCompanyName.trim(),
				address: address.trim(),
				city: city.trim(),
				province: province.trim(),
				country: country.trim(),
				postalCode: postalCode.trim(),
				phoneNumber: phoneNumber.trim(),
				timezone: timezone,
				enableNotificationEmail: enableNotificationEmail,

				useDifferentBillingInfo: !defaultUseSameBillingInfo,

				defaultBillingName: defaultBillingName.trim(),
				defaultBillingAddress: defaultBillingAddress.trim(),
				defaultBillingCity: defaultBillingCity.trim(),
				defaultBillingProvince: defaultBillingProvince.trim(),
				defaultBillingCountry: defaultBillingCountry.trim(),
				defaultBillingPostalCode: defaultBillingPostalCode.trim(),
				defaultBillingPhoneNumber: defaultBillingPhoneNumber.trim(),
				defaultBillingEmail: defaultBillingEmail.trim(),

				defaultTestReportCopyEmails: testReportEmails,
				defaultBillingInfoCopyEmails: billingInfoEmails,
				userLicenses: userLicenses,
			});
			if (response?.userInfo) {
				const userInfo: FrontendUserInfo = response.userInfo;
				localStorage.setItem(
					"billionspectra_userInfo",
					JSON.stringify(response.userInfo)
				);
				props.setUser(userInfo);
				resetUserStatesToDefault(userInfo);
				setUnsavedChanges(false);

				openSnackbar(
					translation("InformationUpdated"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />
				);

				await getUserProfileInfo();
			}
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	};

	const avatarPlaceholder: React.ReactNode = (
		<div className="profile-page-user-avatar">
			<div
				className="typography-heading-01"
				style={{ color: SecondaryColor.SECONDARY_200 }}
			>{`${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`}</div>
		</div>
	);

	const headerCard: React.ReactNode = (
		<Card
			className="common-card card-normal-padding"
			sx={{ backgroundColor: SecondaryColor.SECONDARY_800 }}
		>
			<div className="profile-page-user-info">
				{avatarPlaceholder}
				<div style={{ color: "white" }}>
					<div className="typography-heading-01">{`${user?.firstName} ${user?.lastName}`}</div>
					<div className="typography-caption">{user?.userName}</div>
				</div>
			</div>
		</Card>
	);

	let profileTabs: Array<{
		name: string;
		description?: string;
		value: any;
		children?: React.ReactNode;
		key: string;
	}> = [
		{
			name: translation(FrontendProfileTab.PROFILE),
			value: FrontendProfileTab.PROFILE,
			key: FrontendProfileTab.PROFILE,
			children: (
				<div
					className={`center-text profile-page-radio-option  ${
						currentTab === FrontendProfileTab.PROFILE
							? "profile-page-radio-option-selected"
							: ""
					}`}
				>
					{translation(FrontendProfileTab.PROFILE)}
				</div>
			),
		},

		{
			name: translation(FrontendProfileTab.LICENSE),
			value: FrontendProfileTab.LICENSE,
			key: FrontendProfileTab.LICENSE,
			children: (
				<div
					className={`center-text profile-page-radio-option  ${
						currentTab === FrontendProfileTab.LICENSE
							? "profile-page-radio-option-selected"
							: ""
					}`}
				>
					{translation(FrontendProfileTab.LICENSE)}
				</div>
			),
		},

		{
			name: translation(FrontendProfileTab.BILLING),
			value: FrontendProfileTab.BILLING,
			key: FrontendProfileTab.BILLING,
			children: (
				<div
					className={`center-text profile-page-radio-option  ${
						currentTab === FrontendProfileTab.BILLING
							? "profile-page-radio-option-selected"
							: ""
					}`}
				>
					{translation(FrontendProfileTab.BILLING)}
				</div>
			),
		},
		{
			name: translation(FrontendProfileTab.SPECIFICATIONS),
			value: FrontendProfileTab.SPECIFICATIONS,
			key: FrontendProfileTab.SPECIFICATIONS,
			children: (
				<div
					className={`center-text profile-page-radio-option  ${
						currentTab === FrontendProfileTab.SPECIFICATIONS
							? "profile-page-radio-option-selected"
							: ""
					}`}
				>
					{translation(FrontendProfileTab.SPECIFICATIONS)}
				</div>
			),
		},
	];

	const chooseProfileTab: React.ReactNode = (
		<Card className=" card-border-radius-14p card-small-padding profile-page-tab-selection-card ">
			<CustomRadioGroup
				options={profileTabs}
				selectedOption={currentTab}
				onChange={(e) => setCurrentTab(e.target.value)}
				className="profile-page-radio-group"
			/>
		</Card>
	);
	const actionPanel: React.ReactNode = (
		<div
			className="profile-page-action-panel"
			style={{
				visibility:
					currentTab === FrontendProfileTab.SPECIFICATIONS
						? "hidden"
						: "initial",
			}}
		>
			<OutlinedButton
				key="discard-changes-button"
				id="discard-changes-button"
				children={translation("DiscardChanges")}
				disabled={loading}
				onClick={() => {
					discardChanges();
				}}
				outlineColor={NeutralColor.NEUTRAL_GREY_900}
			></OutlinedButton>

			<BlueButton
				key="save-changes-button"
				id="save-changes-button"
				children={
					loading ? (
						<ButtonTextLoadingAnimation
							text={translation("Loading")}
						></ButtonTextLoadingAnimation>
					) : (
						translation("SaveChanges")
					)
				}
				disabled={loading}
				onClick={() => {
					handleSubmitUserInfo();
				}}
			></BlueButton>
		</div>
	);

	const actionRow: React.ReactNode = (
		<div className="profile-page-action-row">
			{chooseProfileTab}
			{actionPanel}
		</div>
	);

	const clientInformation: React.ReactNode = (
		<UserClientInfoSection
			clientInformationFields={clientInformationFields}
			companyInformationFields1={companyInformationFields1}
			companyInformationFields2={companyInformationFields2}
			country={country}
			setCountry={setCountry}
			province={province}
			setProvince={setProvince}
			testReportEmails={testReportEmails}
			enableNotificationEmail={enableNotificationEmail}
			timezone={timezone}
			setTimezone={setTimezone}
			setEnableNotificationEmail={setEnableNotificationEmail}
			setTestReportEmails={setTestReportEmails}
			setChangePasswordModalOpen={setChangePasswordModalOpen}
			testReportEmailsErrorMsgs={testReportEmailsErrorMsgs}
			setTestReportEmailsErrorMsgs={setTestReportEmailsErrorMsgs}
			setUnsavedChanges={setUnsavedChanges}
		/>
	);
	const licenseInformation: React.ReactNode = (
		<UserLicenseInfoSection
			userLicenses={userLicenses}
			setUserLicenses={setUserLicenses}
			setUnsavedChanges={setUnsavedChanges}
		></UserLicenseInfoSection>
	);

	const billingInformation: React.ReactNode = (
		<UserBillingInfoSection
			billingInformationFields1={billingInformationFields1}
			billingInformationFields2={billingInformationFields2}
			billingInformationFields3={billingInformationFields3}
			defaultBillingCountry={defaultBillingCountry}
			setDefaultBillingCountry={setDefaultBillingCountry}
			defaultBillingProvince={defaultBillingProvince}
			setDefaultBillingProvince={setDefaultBillingProvince}
			billingInfoEmails={billingInfoEmails}
			setBillingInfoEmails={setBillingInfoEmails}
			billingInfoEmailsErrorMsgs={billingInfoEmailsErrorMsgs}
			setBillingInfoEmailsErrorMsgs={setBillingInfoEmailsErrorMsgs}
			defaultUseSameBillingInfo={defaultUseSameBillingInfo}
			setDefaultUseSameBillingInfo={setDefaultUseSameBillingInfo}
			setUnsavedChanges={setUnsavedChanges}
		></UserBillingInfoSection>
	);

	const specificationInformation: React.ReactNode = (
		<UserSpecificationsSection
			userSpecifications={userSpecifications}
			setUserSpecifications={setUserSpecifications}
		></UserSpecificationsSection>
	);

	let information: React.ReactNode = <></>;
	switch (currentTab) {
		case FrontendProfileTab.PROFILE:
			information = clientInformation;
			break;
		case FrontendProfileTab.LICENSE:
			information = licenseInformation;
			break;
		case FrontendProfileTab.BILLING:
			information = billingInformation;
			break;
		case FrontendProfileTab.SPECIFICATIONS:
			information = specificationInformation;
			break;
	}

	const informationCard: React.ReactNode = (
		<Card className="common-card card-normal-padding">{information}</Card>
	);
	return (
		<div style={{ margin: "16px 24px 16px" }}>
			{headerCard}
			{actionRow}
			{informationCard}
			<ChangePasswordModal
				userName={user?.userName as string}
				open={changePasswordModalOpen}
				setOpen={setChangePasswordModalOpen}
			></ChangePasswordModal>
		</div>
	);
}
